import { useEffect, useRef, useState } from 'react';
import { useT } from '../hooks/useT';
import { useSettings } from '../state/SettingsContext';

interface QuestionImageProps {
  src: string;
  altAr: string;
  altEn: string;
}

/**
 * Shows the original picture exactly as supplied — never cropped, redrawn or
 * replaced. It scales to the screen but keeps its aspect ratio, and tapping it
 * opens a full-size view.
 */
export function QuestionImage({ src, altAr, altEn }: QuestionImageProps) {
  const t = useT();
  const { uiLang } = useSettings();
  const [open, setOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const alt = uiLang === 'ar' ? altAr || altEn : altEn || altAr;

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div>
      <div className="question-image">
        <button
          type="button"
          className="question-image__button"
          onClick={() => setOpen(true)}
          aria-label={`${alt} — ${t('enlargeImage')}`}
        >
          <img src={src} alt={alt} />
        </button>
      </div>
      <p className="image-hint">{t('enlargeImage')}</p>

      {open ? (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={alt}
          onClick={() => setOpen(false)}
        >
          <button ref={closeRef} type="button" className="btn lightbox__close" onClick={() => setOpen(false)}>
            <span aria-hidden="true">✕</span> {t('close')}
          </button>
          <img className="lightbox__img" src={src} alt={alt} onClick={(e) => e.stopPropagation()} />
        </div>
      ) : null}
    </div>
  );
}
